import type { Locale } from '$lib/i18n/types';
import type { TripOptions } from '../search/itinerary/tripOptionsStore';
import { page } from '$app/stores';
import {
	type CarEnergy$options,
	Currency,
	type Currency$options,
	DistanceSystem,
	type DistanceSystem$options,
	ItineraryConstraint,
	type ItineraryConstraint$options,
	ItineraryTrafficMode,
	ItineraryTravelMode,
	type ItineraryTravelMode$options,
} from '$houdini';
import { carEnergies } from '$lib/i18n/computed/carEnergies';
import * as c from '$lib/i18n/computedMessages';
import { get } from 'svelte/store';
import {
	currenciesList,
	distanceSystemsList,
	travelModesList,
	tripConstraintsList,
} from './constant';

const defaultDistanceSystem: Record<Locale, DistanceSystem$options> = {
	'fr-FR': DistanceSystem.METRIC,
	'fr-BE': DistanceSystem.METRIC,
	'fr-CH': DistanceSystem.METRIC,
	'nl-BE': DistanceSystem.METRIC,
	'de-DE': DistanceSystem.METRIC,
	'de-AT': DistanceSystem.METRIC,
	'de-CH': DistanceSystem.METRIC,
	'en-US': DistanceSystem.IMPERIAL,
	'en-GB': DistanceSystem.IMPERIAL,
	'en-IE': DistanceSystem.METRIC,
	'es-ES': DistanceSystem.METRIC,
	'it-IT': DistanceSystem.METRIC,
	'it-CH': DistanceSystem.METRIC,
	'nl-NL': DistanceSystem.METRIC,
	'pt-PT': DistanceSystem.METRIC,
};

// TO DO : re use the defaultCurrency after the update of the petrol price service
// Temporary solution to set the default currency and respect the lint rule 'no-unused-vars'
const _defaultCurrency: Record<Locale, Currency$options> = {
	'fr-FR': Currency.eur,
	'fr-BE': Currency.eur,
	'fr-CH': Currency.chf,
	'nl-BE': Currency.eur,
	'de-DE': Currency.eur,
	'de-AT': Currency.eur,
	'de-CH': Currency.chf,
	'en-US': Currency.usd,
	'en-GB': Currency.gbp,
	'en-IE': Currency.eur,
	'es-ES': Currency.eur,
	'it-IT': Currency.eur,
	'it-CH': Currency.chf,
	'nl-NL': Currency.eur,
	'pt-PT': Currency.eur,
};

export const DEFAULT_LEVEL_OF_CHARGE = 20;

export function getDefaultTripOptions(): TripOptions {
	const pageData = get(page);
	const locale: Locale = pageData.data.locale;

	return {
		tripConstraint: ItineraryConstraint.NONE,
		travelMode: ItineraryTravelMode.CAR,
		distanceSystem: defaultDistanceSystem[locale],
		currency: Currency.eur, // TO DO : re use the defaultCurrency after the update of the petrol price service
		energyPrice: undefined,
		rechargeTreshold: DEFAULT_LEVEL_OF_CHARGE,
		traffic: ItineraryTrafficMode.CLOSINGS,
	};
}

export interface TripOptionCar {
	id: string;
	isDefault: boolean;
	isEv: boolean;
	energy: CarEnergy$options;
	model: string;
	maker: string;
}

export const tripOptionsCarName = {
	separator: ' - ',
	serialize(car: { model: string; energy: CarEnergy$options }): string {
		return `${car.model}${tripOptionsCarName.separator}${carEnergies(car.energy)}`;
	},
	parse(carName: string): { model: string; energy: CarEnergy$options } {
		const [model, energy] = carName.split(tripOptionsCarName.separator);
		return {
			model,
			energy: energy as CarEnergy$options,
		};
	},
};

const travelModeIcon: Record<ItineraryTravelMode$options, string> = {
	CAR: 'car',
	MOTORCYCLE: 'motorcycle',
	BIKE: 'bike',
	WALK: 'walk',
};
export function getTravelModeOptions(): {
	key: ItineraryTravelMode$options;
	label: string;
	icon: string;
}[] {
	return travelModesList.map(mode => ({
		key: mode,
		label: c.travelModes(mode),
		icon: travelModeIcon[mode],
	}));
}

export function getTripConstraintOptions(): {
	value: ItineraryConstraint$options;
	label: string;
}[] {
	return tripConstraintsList.map(constraint => ({
		value: constraint,
		label: c.tripConstraints(constraint),
	}));
}

export function getDistanceSystemOptions(): { key: DistanceSystem$options; label: string }[] {
	return distanceSystemsList.map(du => ({
		key: du,
		label: c.distanceSystems(du),
	}));
}

export function getCurrencyOptions(): {
	value: Currency$options;
	label: string;
}[] {
	return currenciesList.map(currency => ({
		value: currency,
		label: c.currencies(currency),
	}));
}
